import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBillingMarginQuery } from '@/queries/billing'
import type { BillingReportGroup } from '@/types/billing'
import { formatMoney } from './money'

function thisMonth(): { start: string; end: string } {
  const now = new Date()
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  return { start: start.toISOString(), end: end.toISOString() }
}

/** What the page is opened for: how much was sold, what it cost, what is left. */
export function MarginSection({ accountId }: { accountId: number }) {
  const { t, i18n } = useTranslation()
  const [group, setGroup] = React.useState<BillingReportGroup>('total')
  const period = React.useMemo(thisMonth, [])
  const { data, isLoading, isError } = useBillingMarginQuery(accountId, period.start, period.end, group)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t('billing.margin.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('billing.margin.period_this_month')}</p>
        </div>
        <Select value={group} onValueChange={(value) => setGroup(value as BillingReportGroup)}>
          <SelectTrigger className="w-48" aria-label={t('billing.margin.group_label')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="total">{t('billing.margin.group_total')}</SelectItem>
            <SelectItem value="pack">{t('billing.margin.group_pack')}</SelectItem>
            <SelectItem value="person">{t('billing.margin.group_person')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : isError ? (
        <p className="text-sm text-destructive">{t('billing.errors.load')}</p>
      ) : !data || data.rows.length === 0 ? (
        <EmptyState title={t('billing.margin.empty_title')} description={t('billing.margin.empty_description')} />
      ) : (
        <div className="space-y-2">
          {data.rows.map((row) => (
            <div key={row.key} className="grid gap-4 rounded-lg border p-4 md:grid-cols-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{group === 'total' ? t('billing.margin.group_total') : row.key}</p>
                <p className="text-2xl font-semibold tracking-tight">
                  {row.margin ? formatMoney(row.margin, i18n.language) : '—'}
                </p>
                <p className="text-xs text-muted-foreground">{t('billing.margin.margin')}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('billing.margin.revenue')}</p>
                {row.revenue.length === 0 ? <p className="text-sm">—</p> : row.revenue.map((m) => (
                  <p key={m.currency} className="text-sm">{formatMoney(m, i18n.language)}</p>
                ))}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('billing.margin.cost')}</p>
                {row.cost.length === 0 ? <p className="text-sm">—</p> : row.cost.map((m) => (
                  <p key={m.currency} className="text-sm">{formatMoney(m, i18n.language)}</p>
                ))}
                {row.cost_is_partial && (
                  <p className="text-xs text-warning">
                    {t('billing.margin.partial', { count: row.usages_without_cost })}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('billing.margin.usages')}</p>
                <p className="text-sm">{row.usages}</p>
                {row.margin_unavailable_because && (
                  <p className="text-xs text-muted-foreground">{t('billing.margin.no_fx')}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
